/**
 * Sticky buy bar.
 *
 * Shows once the main add-to-cart scrolls out of view. Deliberately thin: the
 * form inside is a real Dawn <product-form>, so adding to cart goes through
 * product-form.js and the cart drawer and cart bubble update themselves. The
 * only state this owns is "am I visible" and "which variant is selected", and
 * even the second is mirrored from the main picker rather than owned.
 */
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-sticky-bar]');
    this.select = this.querySelector('[data-sticky-variant-select]');
    this.idInput = this.querySelector('input[name="id"]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.submit = this.querySelector('button[type="submit"]');
    this.sectionId = this.dataset.sectionId;

    this.setupObserver();
    this.setupVariantSync();
    this.setupHeightVar();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribe?.();
    this.resizeObserver?.disconnect();
  }

  /**
   * Watch the main buy button. While it is on screen the sticky bar is
   * redundant, so hide it. Falls back to showing the bar immediately if the
   * target never appears — better a always-visible bar than no buy button.
   */
  setupObserver() {
    const targetId = this.dataset.observeTarget;
    const target = targetId ? document.getElementById(targetId) : null;

    if (!target) {
      this.toggle(true);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.toggle(!entry.isIntersecting)),
      { rootMargin: '0px 0px -40px 0px' }
    );
    this.observer.observe(target);
  }

  toggle(visible) {
    this.bar?.classList.toggle('sticky-buy__bar--visible', visible);
    this.bar?.setAttribute('aria-hidden', visible ? 'false' : 'true');
    document.body.classList.toggle('has-sticky-buy', visible);
  }

  /**
   * Mirror the main variant picker. product-info.js publishes variant-change
   * with the re-rendered section HTML; the price is lifted from that rather
   * than reformatted here, so currency and compare-at markup stay identical to
   * the main product block.
   */
  setupVariantSync() {
    if (typeof subscribe !== 'function') return;

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant) return;

      if (this.idInput) this.idInput.value = variant.id;
      if (this.select) this.select.value = String(variant.id);

      const html = event.data.html;
      const freshPrice = html?.querySelector(`#price-${event.data.sectionId}`);
      if (freshPrice && this.priceEl) this.priceEl.innerHTML = freshPrice.innerHTML;

      if (this.submit) {
        this.submit.disabled = !variant.available;
        const label = this.submit.querySelector('span');
        if (label) {
          label.textContent = variant.available
            ? this.dataset.labelAvailable
            : this.dataset.labelSoldOut;
        }
      }
    });

    // The bar's own select is a shortcut, not a second source of truth: it
    // writes to the form and lets the main picker catch up via the URL.
    this.select?.addEventListener('change', () => {
      if (this.idInput) this.idInput.value = this.select.value;
      const option = this.select.selectedOptions[0];
      if (this.submit) this.submit.disabled = option?.dataset.available === 'false';
    });
  }

  /** Reserve space so the bar never sits on top of the footer or cart button. */
  setupHeightVar() {
    if (!this.bar || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sticky-buy-height', `${this.bar.offsetHeight}px`);
    });
    this.resizeObserver.observe(this.bar);
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
